import { useState } from "react";
import { type AuthUser } from "wasp/auth";
import { getAdminLayouts, setEdgelyFlag, useQuery, useAction } from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import { Button } from "../../../client/components/ui/button";
import { Badge } from "../../../client/components/ui/badge";
import { Input } from "../../../client/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../client/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../client/components/ui/select";
import { useToast } from "../../../client/hooks/use-toast";
import { LayoutTemplate, Pencil, Star, StarOff } from "lucide-react";
import useTranslation from "../../../client/hooks/useTranslation";
import { cn } from "../../../client/utils";

type MetaDialogState = {
  layoutId: string;
  displayName: string;
  description: string;
  requiredPlan: "" | "Pro" | "Max";
} | null;

export default function AdminLayoutsPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"all" | "dashboard" | "pfm" | "custom-page">("all");
  const [metaDialog, setMetaDialog] = useState<MetaDialogState>(null);
  const [confirmUnmark, setConfirmUnmark] = useState<string | null>(null);

  const { data: layouts, refetch } = useQuery(getAdminLayouts, {
    search: search || undefined,
    scope: scope === "all" ? undefined : scope,
  });

  const flagAction = useAction(setEdgelyFlag);

  const handleToggleEdgely = async (layoutId: string, current: boolean) => {
    if (current) {
      setConfirmUnmark(layoutId);
      return;
    }
    await doSetFlag(layoutId, true);
  };

  const doSetFlag = async (layoutId: string, isEdgely: boolean, displayName?: string, description?: string) => {
    try {
      await flagAction({ layoutId, isEdgely, shareDisplayName: displayName, shareDescription: description });
      toast({ title: t.adminLayouts.flagUpdated });
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  const handleSaveMeta = async () => {
    if (!metaDialog) return;
    try {
      await flagAction({
        layoutId: metaDialog.layoutId,
        isEdgely: true,
        shareDisplayName: metaDialog.displayName,
        shareDescription: metaDialog.description,
        requiredPlan: metaDialog.requiredPlan === "" ? null : metaDialog.requiredPlan,
      });
      toast({ title: t.adminLayouts.saveMetaSuccess });
      setMetaDialog(null);
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={t.adminLayouts.title} />

      <div className="flex items-center gap-3 mb-6">
        <Input
          placeholder={t.adminLayouts.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select value={scope} onValueChange={(v) => setScope(v as any)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.adminLayouts.scopeAll}</SelectItem>
            <SelectItem value="dashboard">Dashboard</SelectItem>
            <SelectItem value="pfm">PropFirm</SelectItem>
            <SelectItem value="custom-page">Custom page</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!layouts || layouts.length === 0 ? (
        <p className="text-muted-foreground">{t.adminLayouts.noLayouts}</p>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">{t.adminLayouts.owner}</th>
                <th className="px-4 py-3 text-left font-medium">Scope</th>
                <th className="px-4 py-3 text-left font-medium">{t.adminLayouts.widgets}</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {layouts.map((l) => (
                <tr key={l.id} className={cn("border-t transition-colors hover:bg-muted/30", l.isEdgely && "bg-primary/5")}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.name}</div>
                    {l.shareDisplayName && (
                      <div className="text-muted-foreground text-xs">{l.shareDisplayName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{l.ownerUsername ?? "—"}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-xs">{l.scope}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{l.widgetCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {l.isEdgely && (
                        <Badge className="text-xs bg-primary/20 text-primary border-primary/30">
                          <LayoutTemplate size={10} className="mr-1" />
                          {t.adminLayouts.edgelyBadge}
                        </Badge>
                      )}
                      {l.requiredPlan && (
                        <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30">
                          {l.requiredPlan}
                        </span>
                      )}
                      {l.isShared && (
                        <Badge variant="secondary" className="text-xs">{t.adminLayouts.sharedBadge}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {l.isEdgely && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => setMetaDialog({ layoutId: l.id, displayName: l.shareDisplayName ?? "", description: l.shareDescription ?? "", requiredPlan: (l.requiredPlan as "" | "Pro" | "Max") ?? "" })}
                        >
                          <Pencil size={12} className="mr-1" />
                          {t.adminLayouts.editMeta}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={l.isEdgely ? "destructive" : "outline"}
                        className="h-7 px-2 text-xs"
                        onClick={() => handleToggleEdgely(l.id, l.isEdgely)}
                      >
                        {l.isEdgely ? (
                          <><StarOff size={12} className="mr-1" />{t.adminLayouts.unmarkEdgely}</>
                        ) : (
                          <><Star size={12} className="mr-1" />{t.adminLayouts.markEdgely}</>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Meta edit dialog */}
      <Dialog open={!!metaDialog} onOpenChange={(o) => !o && setMetaDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminLayouts.editMeta}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">{t.adminLayouts.displayName}</label>
              <Input
                value={metaDialog?.displayName ?? ""}
                onChange={(e) => setMetaDialog((prev) => prev ? { ...prev, displayName: e.target.value } : prev)}
                maxLength={80}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t.adminLayouts.description}</label>
              <Input
                value={metaDialog?.description ?? ""}
                onChange={(e) => setMetaDialog((prev) => prev ? { ...prev, description: e.target.value } : prev)}
                maxLength={200}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t.adminLayouts.requiredPlan}</label>
              <Select
                value={metaDialog?.requiredPlan === "" ? "none" : (metaDialog?.requiredPlan ?? "none")}
                onValueChange={(v) => setMetaDialog((prev) => prev ? { ...prev, requiredPlan: v === "none" ? "" : v as "Pro" | "Max" } : prev)}
              >
                <SelectTrigger className="h-8 text-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.adminLayouts.allPlans}</SelectItem>
                  <SelectItem value="Pro">Pro</SelectItem>
                  <SelectItem value="Max">Max</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMetaDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveMeta}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm unmark dialog */}
      <Dialog open={!!confirmUnmark} onOpenChange={(o) => !o && setConfirmUnmark(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminLayouts.confirmUnmark}</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUnmark(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!confirmUnmark) return;
                await doSetFlag(confirmUnmark, false);
                setConfirmUnmark(null);
              }}
            >
              {t.adminLayouts.unmarkEdgely}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DefaultLayout>
  );
}
